import React, { useCallback } from "react";
import { motion } from "framer-motion";
import { FileTextIcon, ZapIcon, CreditCardIcon, GiftIcon } from "lucide-react";
import { MetaPixel } from "./components/MetaPixel";
import { BuyButton } from "./components/BuyButton";
import { Accordion } from "./components/Accordion";
import { GmailReviews } from "./components/GmailReviews";
import { ComparisonTable } from "./components/ComparisonTable";
import { TestimonialCarousel } from "./components/TestimonialCarousel";
import { PurchasePopup } from "./components/PurchasePopup";
import { FinalOffer } from "./components/FinalOffer";
import { Objections } from "./components/Objections";
import { BonusCarousel } from "./components/BonusCarousel";
import { CHECKOUT_URL, PRODUCT_IMAGE, COURT_BG, PROBLEMS, MODULES } from "./data/content";
export function App() {
  const goToCheckout = useCallback(() => {
    window.open(CHECKOUT_URL, "_blank", "noopener,noreferrer");
  }, []);
  const scrollToBuy = useCallback(() => {
    document.getElementById("compra")?.scrollIntoView({
      behavior: "smooth"
    });
  }, []);
  return <div className="relative min-h-screen w-full overflow-x-hidden bg-[#050505] text-white" data-id="element-0" data-mp="3mkrchhuehbbq">
      <MetaPixel data-id="element-1" data-mp="3rbzmog3k10py" />

      {/* Blurred padel court background */}
      <div className="pointer-events-none fixed inset-0 z-0" data-id="element-2" data-mp="3relvsk63foaa">
        <img src={COURT_BG} alt="" aria-hidden="true" className="h-full w-full object-cover blur-[6px] scale-110" data-id="element-3" data-mp="5gel8p3rovc" />
        <div className="absolute inset-0 bg-black/80" data-id="element-4" data-mp="33m5c656pjo8v" />
      </div>

      {/* Content — mobile-first, centered column */}
      <main className="relative z-10 mx-auto w-full max-w-md" data-id="element-5" data-mp="38kqgafp2ys2l">
        {/* HERO */}
        <section className="px-5 pt-10 pb-6" data-id="element-6" data-mp="3dl4llpzarnwx">
          <div className="mb-4 flex justify-center" data-id="element-7" data-mp="3dirtwl3qlz6o">
            <span className="rounded-full border border-[#d4ff00]/40 bg-[#d4ff00]/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-[#d4ff00]" data-id="element-8" data-mp="nwc5m3lqig1j">
              🎾 Solo para jugadores zurdos
            </span>
          </div>

          <h1 className="text-center font-display text-4xl uppercase leading-[0.95] text-white" data-id="element-9" data-mp="34ndfy2z19zs">
            Domina el pádel en{" "}
            <span className="text-[#d4ff00]" data-id="element-10" data-mp="d4e75xjpjkyt">8 semanas</span>, con la ventaja del
            zurdo
          </h1>

          {/* Primera imagen — producto principal, clickable */}
          <motion.button type="button" onClick={scrollToBuy} initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5
        }} className="mt-6 block w-full" aria-label="Ir a la sección de compra" data-id="element-11" data-mp="83qhbf77ta66">
            <img src={PRODUCT_IMAGE} alt="Padel Zurdo Pro — sistema completo para jugadores zurdos" className="w-full rounded-2xl border border-white/10 shadow-2xl" data-id="element-12" data-mp="3ljxu6etsc75f" />
          </motion.button>

          {/* Subtítulo */}
          <p className="mt-6 text-center text-base font-semibold leading-snug text-white/85" data-id="element-13" data-mp="18drjdvxkvfdt">
            7 Módulos, +15 drills y +20 recursos tácticos, todo en un solo lugar.
          </p>

          {/* Badges */}
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2" data-id="element-14" data-mp="yfv6ozozscdl">
            {[{
            icon: <FileTextIcon className="h-3.5 w-3.5" data-id="element-15" data-mp="13e565mhr1z50" />,
            t: "100% PDF"
          }, {
            icon: <ZapIcon className="h-3.5 w-3.5" data-id="element-16" data-mp="1r495virtheqq" />,
            t: "Entrega Inmediata"
          }, {
            icon: <CreditCardIcon className="h-3.5 w-3.5" data-id="element-17" data-mp="1r2g0s7wur2ku" />,
            t: "Pago Único"
          }].map((b, i) => <span key={i} className="flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-[12px] font-medium text-white/80" data-id="element-18" data-mp="1i3ku6upwbqh0" data-mpi={i}>
                {b.icon}
                {b.t}
              </span>)}
          </div>

          {/* Primer botón de compra */}
          <div className="mt-6" data-id="element-19" data-mp="aqsita3rlnan">
            <BuyButton label="Quiero dominar mi zurda" onClick={scrollToBuy} data-id="element-20" data-mp="anwkvlrx2ym7" />
          </div>
        </section>

        {/* GMAIL REVIEWS */}
        <section className="px-5 py-6" data-id="element-21" data-mp="m5xa1b8jzj1j">
          <p className="mb-4 text-center text-[13px] font-bold uppercase tracking-widest text-white/50" data-id="element-22" data-mp="m547ynkjhhpq">
            Lo que nos escriben los jugadores
          </p>
          <GmailReviews data-id="element-23" data-mp="c90x8tbmgar6" />
        </section>

        {/* PROBLEMS */}
        <section className="px-5 py-8" data-id="element-24" data-mp="19vzt51uhpeo0">
          <h2 className="text-center font-display text-2xl uppercase leading-tight text-white" data-id="element-25" data-mp="19uygisxtsk46">
            Si juegas al pádel como un diestro, siendo zurdo probablemente te ha
            sucedido algo de esto:
          </h2>
          <ul className="mt-5 flex flex-col gap-3" data-id="element-26" data-mp="zw0p9i90mchh">
            {PROBLEMS.map((p, i) => <li key={i} className="flex gap-3 rounded-2xl border border-red-500/20 bg-red-500/[0.05] p-3.5" data-id="element-27" data-mp="vvyuzu5m0lc8" data-mpi={i}>
                <span className="text-lg" data-id="element-28" data-mp="3gm7rrnvyqviy">❌</span>
                <span className="text-[13.5px] leading-snug text-white/80" data-id="element-29" data-mp="3gok6ls6fcprs">
                  {p}
                </span>
              </li>)}
          </ul>
        </section>

        {/* SOLUTION INTRO + MODULES */}
        <section className="px-5 py-8" data-id="element-30" data-mp="2553elafhqxw9">
          <div className="mb-5 rounded-2xl border border-[#d4ff00]/30 bg-[#d4ff00]/[0.07] p-5 text-center" data-id="element-31" data-mp="254bk62xmr67k">
            <p className="text-base font-bold leading-snug text-white" data-id="element-32" data-mp="29xeggitsktrg">
              Por eso hemos creado
            </p>
            <p className="mt-1 whitespace-nowrap font-display text-[clamp(1.75rem,8vw,2.25rem)] uppercase leading-none text-[#d4ff00]" data-id="element-33" data-mp="2ex0swq1sq5z7">
              Padel Zurdo Pro
            </p>
            <p className="mt-3 text-[13px] text-white/70" data-id="element-34" data-mp="2nw4h28ftlbkk">
              El sistema completo para jugadores zurdos — 7 tomos que convierten
              tu mano dominante en tu mayor ventaja.
            </p>
          </div>

          <p className="mb-3 text-center text-[13px] font-bold uppercase tracking-widest text-white/50" data-id="element-35" data-mp="tjy4b57a2mq5">
            Índice completo del sistema
          </p>
          <Accordion data={MODULES} data-id="element-36" data-mp="21sdfy6d1lvia" />

          <div className="mt-6" data-id="element-37" data-mp="21tf153nrvydq">
            <BuyButton label="Quiero dominar mi zurda" onClick={scrollToBuy} data-id="element-38" data-mp="1wt0w7uhgx4jx" />
          </div>
        </section>

        {/* PUNCH LINE */}
        <section className="px-5 py-8" data-id="element-39" data-mp="2attmg30qldh8">
          <p className="text-center font-display text-2xl uppercase leading-tight text-[#d4ff00]" data-id="element-40" data-mp="25tffkoavg9ks">
            “Mientras sigas copiando el juego de un diestro, vas a seguir jugando
            con el freno de mano puesto.”
          </p>
        </section>

        {/* BONUSES */}
        <section className="px-5 py-8" data-id="element-41" data-mp="2fpismp79hsmi">
          <div className="mb-4 flex items-center justify-center gap-2" data-id="element-42" data-mp="2tizzpairhcvn">
            <GiftIcon className="h-5 w-5 text-[#d4ff00]" data-id="element-43" data-mp="2ph4ye60tyzhv" />
            <h2 className="text-center font-display text-2xl uppercase text-white" data-id="element-44" data-mp="2phoj1uhv81zl">
              Además llévate incluido con tu compra:
            </h2>
          </div>

          <div className="mb-5 rounded-full bg-[#d4ff00] px-4 py-2 text-center text-[13px] font-extrabold uppercase text-black" data-id="element-45" data-mp="2yimgftxstb97">
            🎁 10 bonos GRATIS solo por hoy
          </div>

          <BonusCarousel onSelect={scrollToBuy} data-id="element-46" data-mp="3n396wr1anzbw" />

          <div className="mt-6" data-id="element-47" data-mp="3n0n392hiy3yy">
            <BuyButton label="Quiero dominar mi zurda" onClick={scrollToBuy} data-id="element-48" data-mp="385p7s0r2wydn" />
          </div>
        </section>

        {/* COMPARISON */}
        <section className="px-5 py-8" data-id="element-49" data-mp="3d38kuxc1j540">
          <ComparisonTable data-id="element-50" data-mp="3d6cw2enxb146" />
        </section>

        {/* CAROUSEL TESTIMONIALS */}
        <section className="px-5 py-8" data-id="element-51" data-mp="2zb28lmc0di9u">
          <h2 className="mb-5 text-center font-display text-2xl uppercase text-white" data-id="element-52" data-mp="344djbyjxyj71">
            Zurdos que ya juegan diferente
          </h2>
          <TestimonialCarousel data-id="element-53" data-mp="2pgsfu3j5mhyl" />
        </section>

        {/* FINAL OFFER */}
        <FinalOffer onBuy={goToCheckout} data-id="element-54" data-mp="2uetwkht3y56w" />

        {/* OBJECTIONS + SUPPORT */}
        <Objections data-id="element-55" data-mp="2ge11nvyw9839" />

        {/* FOOTER */}
        <footer className="px-5 pb-24 pt-4 text-center" data-id="element-56" data-mp="2ggds6xipwqrx">
          <p className="font-display text-xl uppercase text-white/80" data-id="element-57" data-mp="2keta4mzxz8yh">
            Padel Zurdo Pro
          </p>
          <p className="mt-2 text-[11px] text-white/40" data-id="element-58" data-mp="30u2i8f9jw1g4">
            © {new Date().getFullYear()} EbookDreamShop. Todos los derechos
            reservados.
          </p>
        </footer>
      </main>

      {/* Purchase social-proof popup */}
      <PurchasePopup data-id="element-59" data-mp="2qz0t3jf9yzdj" />

      {/* Sticky buy bar */}
      <div className="fixed inset-x-0 bottom-0 z-40 mx-auto max-w-md border-t border-white/10 bg-black/90 px-4 py-3 backdrop-blur md:hidden" data-id="element-60" data-mp="2lxbe5soxm1k6">
        <button type="button" onClick={scrollToBuy} className="flex w-full items-center justify-between rounded-full bg-[#d4ff00] px-5 py-3 text-black" data-id="element-61" data-mp="2hycf2opvsz0n">
          <span className="flex items-baseline gap-2" data-id="element-62" data-mp="284lo9d6a8chs">
            <span className="text-sm text-black/60 line-through" data-id="element-63" data-mp="238oa9dihri86">50€</span>
            <span className="font-display text-xl leading-none" data-id="element-64" data-mp="1y91yl8ibaa1h">20€</span>
          </span>
          <span className="text-sm font-extrabold uppercase" data-id="element-65" data-mp="1u76yg7c4a2pa">
            Quiero dominar mi zurda
          </span>
        </button>
      </div>
    </div>;
}